import { useNavigation, useRoute } from '@react-navigation/native';
import { SxProp } from 'dripsy';
import { observer } from 'mobx-react-lite';
import React, { useCallback } from 'react';
import {
  EdgeInsets,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';

import {
  screens,
  HubNavigatorNavigationPropType,
  HubNavigatorRoutePropType,
} from '@navigation';
import { IS_WEB } from '@utils';
import {
  TutorialList,
  ITutorialListProps,
  EmptyResultTypeEnum,
  emptyResult,
} from '@components';
import {
  TUTORIAL_EXTRA_CATEGORY_ID_BY_LIST_TYPE,
  TutorialListTypeEnum,
} from '@constants';
import type { Tutorial } from '@models/Tutorials/TutorialModel';
import type { GenericTutorialList } from '@models/Tutorials/GenericTutorialListModel';

function contentContainerStyle(props: EdgeInsets): SxProp {
  return {
    flexGrow: 1,
    px: ['$6', '$0'],
    pb: props.bottom,
  };
}

interface ITutorialsListProps
  extends Pick<ITutorialListProps, 'ListHeaderComponent'> {
  list: GenericTutorialList;
  fetchMore(): Promise<void>;
  refresh(): Promise<void>;
}

type NavigationPropType = HubNavigatorNavigationPropType<'Tutorials'>;
type RoutePropType = HubNavigatorRoutePropType<'Tutorials'>;

export const TutorialsList = observer<ITutorialsListProps>(
  ({ list, fetchMore, refresh, ListHeaderComponent }) => {
    const insets = useSafeAreaInsets();
    const nav = useNavigation<NavigationPropType>();
    const route = useRoute<RoutePropType>();

    const numColumns = IS_WEB ? 4 : 2;

    const navigateToTutorial = useCallback((item: Tutorial) => {
      switch (route.params.type) {
        case TutorialListTypeEnum.History:
        case TutorialListTypeEnum.Saved:
          // navigate to course screen if courseId is present
          if (item.courseId) {
            return nav.push(screens.Tutorial, {
              id: item.id,
              courseId: item.courseId,
            });
          }

          return nav.push(screens.Tutorial, {
            id: item.id,
            extraCategoryId:
              TUTORIAL_EXTRA_CATEGORY_ID_BY_LIST_TYPE[
                route.params.type
              ],
          });
        default:
          return nav.push(screens.Tutorial, {
            id: item.id,
            extraCategoryId:
              TUTORIAL_EXTRA_CATEGORY_ID_BY_LIST_TYPE[
                route.params.type
              ],
          });
      }
    }, []);

    const isLoading = list.fetch.inProgress && list.isEmpty;

    let emptyResultType: EmptyResultTypeEnum =
      EmptyResultTypeEnum.Error;

    if (list.fetch.isError) {
      emptyResultType = EmptyResultTypeEnum.Error;
    } else if (!list.array.length) {
      emptyResultType = emptyResult.getTypeByTutorialListType(
        route.params.type,
      );
    }

    return (
      <TutorialList
        contentContainerSx={contentContainerStyle(insets)}
        numColumns={numColumns}
        data={list.asArray}
        onEndReached={fetchMore}
        onRefresh={refresh}
        isRefreshing={list.fetch.inProgress}
        isLoading={isLoading}
        isLoadingMore={list.fetchMore.inProgress}
        onPress={navigateToTutorial}
        emptyResultType={emptyResultType}
        ListHeaderComponent={ListHeaderComponent}
      />
    );
  },
);
